/**
 * @fileoverview Types and schemas for TikTok Shop integration
 */

import { z } from 'zod'
import { SupportedTikTokShopWebhookTopics } from '../action-auto-trigger.js'

// WEBHOOK -----------------------------------------------------------------------------------------

/**
 * TikTok identifies webhooks using a numeric 'type' field.
 *
 * https://partner.tiktokshop.com/docv2/page/650300b8a57708028b430b4a
 */
export enum TiktokWebhookType {
  /** https://partner.tiktokshop.com/docv2/page/1-order-status-change */
  ORDER_STATUS_CHANGE = 1,
  /** https://partner.tiktokshop.com/docv2/page/7-upcoming-authorization-expiration */
  AUTH_EXPIRE = 7,
  /** https://partner.tiktokshop.com/docv2/page/12-return-status-change */
  FBT_ORDER_RETURN_STATUS_CHANGE = 12,
  /** https://partner.tiktokshop.com/docv2/page/21-inbound-fbt-order-status-change */
  FBT_ORDER_STATUS_UPDATE = 21,
  /** https://partner.tiktokshop.com/docv2/page/22-fbt-merchant-onboarding */
  FBT_MERCHANT_ONBOARDING = 22,
  /** https://partner.tiktokshop.com/docv2/page/23-fbt-goods-match */
  FBT_GOODS_MATCH = 23,
  /** https://partner.tiktokshop.com/docv2/page/24-fbt-inventory-update */
  FBT_INVENTORY_UPDATE = 24,
  /** https://partner.tiktokshop.com/docv2/page/27-inventory-status-change */
  INVENTORY_STATUS_CHANGE = 27,
  /** https://partner.tiktokshop.com/docv2/page/36-invoice-status-change */
  INVOICE_STATUS_CHANGE = 36,

  // Remaining types that are not yet supported
  // REVERSE_STATUS_UPDATE = 2,
  // RECIPIENT_ADDRESS_UPDATE = 3,
  // PACKAGE_UPDATE = 4,
  // PRODUCT_STATUS_CHANGE = 5,
  // SELLER_DEAUTHORIZATION = 6,
  // CANCELLATION_STATUS_CHANGE = 11,
  // NEW_CONVERSATION = 13,
  // NEW_MESSAGE = 14,
  // PRODUCT_INFORMATION_CHANGE = 15,
  // PRODUCT_CREATION = 16,
  // SHOPPABLE_CONTENT_POSTING = 17,
  // PRODUCT_CATEGORY_CHANGE = 18,
  // SIZING_CHART_CHANGE = 19,
  // CREATOR_DEAUTHORIZATION = 20,
  // OPPORTUNITY_MATCHING_STATUS_CHANGE = 25,
  // NEW_MESSAGE_LISTENER = 33,
  // TOKOPEDIA_MIRROR_STATUS_CHANGE = 35,
  // PRODUCT_AUDIT_STATUS_CHANGE = 37,
  // STRIKETHROUGH_PRICE_EXPIRED = 38,
  // ACTIVITY_STATUS_CHANGE = 39,
  // GLOBAL_REPLICATION_STATUS_CHANGE = 51,
  // GLOBAL_LISTING_METHOD_CHANGE = 52,
}

export const tikTokWebhookTypeToTopic: Record<TiktokWebhookType, SupportedTikTokShopWebhookTopics> =
  {
    [TiktokWebhookType.ORDER_STATUS_CHANGE]: SupportedTikTokShopWebhookTopics.ORDER_STATUS_CHANGE,
    [TiktokWebhookType.FBT_ORDER_STATUS_UPDATE]:
      SupportedTikTokShopWebhookTopics.FBT_ORDER_STATUS_UPDATE,
    [TiktokWebhookType.FBT_MERCHANT_ONBOARDING]:
      SupportedTikTokShopWebhookTopics.FBT_MERCHANT_ONBOARDING,
    [TiktokWebhookType.FBT_GOODS_MATCH]: SupportedTikTokShopWebhookTopics.FBT_GOODS_MATCH,
    [TiktokWebhookType.FBT_INVENTORY_UPDATE]: SupportedTikTokShopWebhookTopics.FBT_INVENTORY_UPDATE,
    [TiktokWebhookType.AUTH_EXPIRE]: SupportedTikTokShopWebhookTopics.AUTH_EXPIRE,
    [TiktokWebhookType.FBT_ORDER_RETURN_STATUS_CHANGE]:
      SupportedTikTokShopWebhookTopics.FBT_ORDER_RETURN_STATUS_CHANGE,
    [TiktokWebhookType.INVENTORY_STATUS_CHANGE]:
      SupportedTikTokShopWebhookTopics.INVENTORY_STATUS_CHANGE,
    [TiktokWebhookType.INVOICE_STATUS_CHANGE]:
      SupportedTikTokShopWebhookTopics.INVOICE_STATUS_CHANGE,
  } as const

/**
 * TikTok Shop Order Changed Webhook Schema
 */
const tikTokShopOrderChangedWebhookSchema = z.object({
  type: z.literal(TiktokWebhookType[SupportedTikTokShopWebhookTopics.ORDER_STATUS_CHANGE]),
  tts_notification_id: z.string().optional(),
  shop_id: z.string().optional(),
  timestamp: z.number().optional(),
  data: z
    .object({
      order_id: z.string().optional(),
      order_status: z.string().optional(),
      is_on_hold_order: z.boolean().optional(),
      update_time: z.union([z.string().datetime(), z.number()]).optional(),
    })
    .optional(),
  // This field is injected by the webhook handler after fetching the order details from the API
  order_details: z.object({}).passthrough().optional(),
})

/**
 * TikTok Shop Order Line Item Schema
 */
const tikTokShopOrderLineItemSchema = z.object({
  id: z.string(),
  sku_id: z.string(),
  combined_listing_skus: z
    .array(
      z.object({
        sku_id: z.string(),
        sku_count: z.number(),
        product_id: z.string(),
        seller_sku: z.string(),
      }),
    )
    .optional(),
  product_id: z.string(),
  product_name: z.string(),
  sku_name: z.string(),
  sku_image: z.string().optional(),
  original_price: z.string(),
  sale_price: z.string(),
  platform_discount: z.string().optional(),
  display_status: z.string().optional(),
  cancel_user: z.string().optional(),
  sku_type: z.string(),
  seller_sku: z.string(),
  shipping_provider_id: z.string().optional(),
  seller_discount: z.string().optional(),
  currency: z.string(),
  package_id: z.string().optional(),
  rts_time: z.coerce.string().optional(),
  item_tax: z
    .array(
      z.object({
        tax_type: z.string().optional(),
        tax_amount: z.string().optional(),
        tax_rate: z.string().optional(),
      }),
    )
    .optional(),
  package_status: z.string().optional(),
  shipping_provider_name: z.string().optional(),
  is_gift: z.boolean().optional(),
  cancel_reason: z.string().optional(),
  small_order_fee: z.string().optional(),
  retail_delivery_fee: z.string().optional(),
  tracking_number: z.string().optional(),
  buyer_service_fee: z.string().optional(),
  handling_duration_days: z.string().optional(),
  is_dangerous_good: z.boolean().optional(),
  needs_prescription: z.boolean().optional(),
})

/**
 * TikTok Shop Order Schema
 */
const tikTokShopOrderSchema = z.object({
  id: z.string(),
  cancellation_initiator: z.string().optional(),
  shipping_provider: z.string().optional(),
  shipping_provider_id: z.string().optional(),
  user_id: z.string().optional(),
  status: z.string(),
  rts_time: z.number().optional(),
  payment: z.record(z.string(), z.string()).optional(),
  recipient_address: z.record(z.string(), z.unknown()).optional(),
  buyer_message: z.string().optional(),
  create_time: z.number().optional(),
  tracking_number: z.string().optional(),
  cancel_reason: z.string().optional(),
  rts_sla_time: z.number().optional(),
  paid_time: z.number().optional(),
  fulfillment_type: z.string().optional(),
  seller_note: z.string().optional(),
  tts_sla_time: z.number().optional(),
  cancel_order_sla_time: z.number().optional(),
  update_time: z.number().optional(),
  packages: z.array(z.object({ id: z.string() })).optional(),
  delivery_type: z.string().optional(),
  is_sample_order: z.boolean().optional(),
  warehouse_id: z.string().optional(),
  split_or_combine_tag: z.string().optional(),
  has_updated_recipient_address: z.boolean().optional(),
  cpf: z.string().optional(),
  delivery_option_id: z.string().optional(),
  delivery_sla_time: z.number().optional(),
  payment_method_name: z.string().optional(),
  shipping_due_time: z.number().optional(),
  line_items: z.array(tikTokShopOrderLineItemSchema).optional(),
  shipping_type: z.string().optional(),
  buyer_email: z.string().optional(),
  delivery_time: z.number().optional(),
  need_upload_invoice: z.string().optional(),
  is_cod: z.boolean().optional(),
  request_cancel_time: z.number().optional(),
  delivery_option_required_delivery_time: z.number().optional(),
  delivery_option_name: z.string().optional(),
  is_buyer_request_cancel: z.boolean().optional(),
  delivery_due_time: z.number().optional(),
  collection_time: z.number().optional(),
  is_on_hold_order: z.boolean().optional(),
  cancel_time: z.number().optional(),
  is_replacement_order: z.boolean().optional(),
  replaced_order_id: z.string().optional(),
  collection_due_time: z.number().optional(),
  pick_up_cut_off_time: z.number().optional(),
  fast_dispatch_sla_time: z.number().optional(),
  commerce_platform: z.string().optional(),
  order_type: z.string().optional(),
  release_date: z.number().optional(),
  handling_duration: z
    .object({
      days: z.string(),
      type: z.string(),
    })
    .optional(),
  auto_combine_group_id: z.string().optional(),
  cpf_name: z.string().optional(),
  is_exchange_order: z.boolean().optional(),
  exchange_source_order_id: z.string().optional(),
  consultation_id: z.string().optional(),
  fast_delivery_program: z.string().optional(),
})

/**
 * TikTok Shop Order API Response Schema
 */
export const tikTokShopOrderApiResponseSchema = z.object({
  code: z.number(),
  message: z.string(),
  data: z.object({
    orders: z.array(tikTokShopOrderSchema),
  }),
})

export type TikTokShopOrderApiResponse = z.infer<typeof tikTokShopOrderApiResponseSchema>

/**
 * TikTok Shop Cipher Response Schema
 */
export const tikTokShopCipherResponseSchema = z.object({
  code: z.number(),
  message: z.string(),
  data: z.object({
    shops: z.array(
      z.object({
        id: z.string(),
        name: z.string(),
        region: z.string(),
        seller_type: z.string(),
        cipher: z.string(),
        code: z.string(),
      }),
    ),
  }),
})

/**
 * TikTok Shop Order Line Item Schema
 */
const tikTokShopOrderLineItemTransformedSchema = z.object({
  id: z.string(),
  sku_id: z.string(),
  combined_listing_skus: z
    .array(
      z.object({
        sku_id: z.string(),
        sku_count: z.number(),
        product_id: z.string(),
        seller_sku: z.string(),
      }),
    )
    .optional(),
  product_id: z.string(),
  product_name: z.string(),
  sku_name: z.string(),
  sku_image: z.string().optional(),
  original_price: z.number(),
  sale_price: z.number(),
  platform_discount: z.number().optional(),
  display_status: z.string().optional(),
  cancel_user: z.string().optional(),
  sku_type: z.string(),
  seller_sku: z.string(),
  shipping_provider_id: z.string().optional(),
  seller_discount: z.number().optional(),
  currency: z.string(),
  package_id: z.string().optional(),
  rts_time: z.coerce.string().optional(),
  item_tax: z
    .array(
      z.object({
        tax_type: z.string().optional(),
        tax_amount: z.number().optional(),
        tax_rate: z.number().optional(),
      }),
    )
    .optional(),
  package_status: z.string().optional(),
  shipping_provider_name: z.string().optional(),
  is_gift: z.boolean().optional(),
  cancel_reason: z.string().optional(),
  small_order_fee: z.number().optional(),
  retail_delivery_fee: z.number().optional(),
  tracking_number: z.string().optional(),
  buyer_service_fee: z.number().optional(),
  handling_duration_days: z.string().optional(),
  is_dangerous_good: z.boolean().optional(),
  needs_prescription: z.boolean().optional(),
})

/**
 * TikTok Shop Order Schema
 */
const tikTokShopOrderTransformedSchema = z.object({
  id: z.string(),
  cancellation_initiator: z.string().optional(),
  shipping_provider: z.string().optional(),
  shipping_provider_id: z.string().optional(),
  user_id: z.string().optional(),
  status: z.string(),
  rts_time: z.coerce.string().optional(),
  payment: z
    .object({
      currency: z.string().optional(),
      sub_total: z.number().optional(),
      shipping_fee: z.number().optional(),
      seller_discount: z.number().optional(),
      platform_discount: z.number().optional(),
      total_amount: z.number().optional(),
      original_total_product_price: z.number().optional(),
      original_shipping_fee: z.number().optional(),
      shipping_fee_seller_discount: z.number().optional(),
      shipping_fee_platform_discount: z.number().optional(),
      shipping_fee_cofunded_discount: z.number().optional(),
      tax: z.number().optional(),
      small_order_fee: z.number().optional(),
      shipping_fee_tax: z.number().optional(),
      product_tax: z.number().optional(),
      retail_delivery_fee: z.number().optional(),
      buyer_service_fee: z.number().optional(),
      handling_fee: z.number().optional(),
      shipping_insurance_fee: z.number().optional(),
      item_insurance_fee: z.number().optional(),
    })
    .optional(),
  recipient_address: z
    .object({
      full_address: z.string().optional(),
      phone_number: z.string().optional(),
      name: z.string().optional(),
      first_name: z.string().optional(),
      last_name: z.string().optional(),
      first_name_local_script: z.string().optional(),
      last_name_local_script: z.string().optional(),
      region_code: z.string().optional(),
      postal_code: z.string().optional(),
      address_line1: z.string().optional(),
      address_line2: z.string().optional(),
      address_line3: z.string().optional(),
      address_line4: z.string().optional(),
      address_detail: z.string().optional(),
      district_info: z
        .array(
          z.object({
            address_level_name: z.string().optional(),
            address_name: z.string().optional(),
            address_level: z.string().optional(),
          }),
        )
        .optional(),
      delivery_preferences: z
        .object({
          drop_off_location: z.string().optional(),
        })
        .optional(),
    })
    .optional(),
  buyer_message: z.string().optional(),
  create_time: z.coerce.string().optional(),
  tracking_number: z.string().optional(),
  cancel_reason: z.string().optional(),
  rts_sla_time: z.coerce.string().optional(),
  paid_time: z.coerce.string().optional(),
  fulfillment_type: z.string().optional(),
  seller_note: z.string().optional(),
  tts_sla_time: z.coerce.string().optional(),
  cancel_order_sla_time: z.coerce.string().optional(),
  update_time: z.coerce.string().optional(),
  packages: z.array(z.object({ id: z.string() })).optional(),
  delivery_type: z.string().optional(),
  is_sample_order: z.boolean().optional(),
  warehouse_id: z.string().optional(),
  split_or_combine_tag: z.string().optional(),
  has_updated_recipient_address: z.boolean().optional(),
  cpf: z.string().optional(),
  delivery_option_id: z.string().optional(),
  delivery_sla_time: z.coerce.string().optional(),
  payment_method_name: z.string().optional(),
  shipping_due_time: z.coerce.string().optional(),
  line_items: z.array(tikTokShopOrderLineItemTransformedSchema).optional(),
  shipping_type: z.string().optional(),
  buyer_email: z.string().optional(),
  delivery_time: z.coerce.string().optional(),
  need_upload_invoice: z.string().optional(),
  is_cod: z.boolean().optional(),
  request_cancel_time: z.coerce.string().optional(),
  delivery_option_required_delivery_time: z.coerce.string().optional(),
  delivery_option_name: z.string().optional(),
  is_buyer_request_cancel: z.boolean().optional(),
  delivery_due_time: z.coerce.string().optional(),
  collection_time: z.coerce.string().optional(),
  is_on_hold_order: z.boolean().optional(),
  cancel_time: z.coerce.string().optional(),
  is_replacement_order: z.boolean().optional(),
  replaced_order_id: z.string().optional(),
  collection_due_time: z.coerce.string().optional(),
  pick_up_cut_off_time: z.coerce.string().optional(),
  fast_dispatch_sla_time: z.coerce.string().optional(),
  commerce_platform: z.string().optional(),
  order_type: z.string().optional(),
  release_date: z.number().optional(),
  handling_duration: z
    .object({
      days: z.string(),
      type: z.string(),
    })
    .optional(),
  auto_combine_group_id: z.string().optional(),
  cpf_name: z.string().optional(),
  is_exchange_order: z.boolean().optional(),
  exchange_source_order_id: z.string().optional(),
  consultation_id: z.string().optional(),
  fast_delivery_program: z.string().optional(),
})

/**
 * TikTok Shop Order status changed webhook data schema
 */
export const tikTokShopOrderStatusChangedWebhookDataSchema = z.union([
  tikTokShopOrderChangedWebhookSchema,
  z.object({
    order_details: tikTokShopOrderTransformedSchema,
  }),
])

/**
 * TikTok Shop FBT Inventory Update Webhook Schema
 * Webhook Documentation: https://partner.tiktokshop.com/docv2/page/24-fbt-inventory-update
 */
const tikTokShopFbtWarehouseOnHandDetailSchema = z
  .object({
    total_quantity: z.number().optional(),
    reserved_quantity: z.number().optional(),
    available_quantity: z.number().optional(),
  })
  .optional()

const tikTokShopFbtWarehouseInventoryItemSchema = z.object({
  fbt_warehouse_id: z.union([z.number(), z.string()]).optional(),
  on_hand_detail: tikTokShopFbtWarehouseOnHandDetailSchema,
})

const tikTokShopFBTInventoryUpdateWebhookSchema = z.object({
  type: z.literal(TiktokWebhookType[SupportedTikTokShopWebhookTopics.FBT_INVENTORY_UPDATE]),
  seller_open_id: z.string(),
  // Present on most TTS webhooks; used to resolve the correct shop_cipher on multi-shop integrations.
  shop_id: z.string().optional(),
  tts_notification_id: z.string(),
  timestamp: z.number(),
  data: z.object({
    goods_id: z.string().optional(),
    sku_id: z.string().optional(),
    // Goods-level pooled quantities from the TikTok webhook (may sum sibling SKUs).
    fbt_warehouse_inventory: z.array(tikTokShopFbtWarehouseInventoryItemSchema).optional(),
    // Injected by the webhook handler from Search FBT Inventory — per-SKU, not pooled.
    granular_fbt_warehouse_inventory: z.array(tikTokShopFbtWarehouseInventoryItemSchema).optional(),
    update_time: z.coerce.string().optional(),
  }),
})

/**
 * TikTok Shop FBT Order Status Update Webhook Schema
 * Webhook Documentation: https://partner.tiktokshop.com/docv2/page/21-inbound-fbt-order-status-change
 */
const tikTokShopFBTOrderStatusUpdateWebhookSchema = z.object({
  type: z.literal(TiktokWebhookType[SupportedTikTokShopWebhookTopics.FBT_ORDER_STATUS_UPDATE]),
  seller_open_id: z.string(),
  tts_notification_id: z.string(),
  timestamp: z.number(),
  data: z.object({
    inbound_order_id: z.string().optional(),
    order_status: z.string().optional(),
    update_time: z.coerce.string().optional(),
  }),
})

/**
 * TikTok Shop FBT Order Status Update webhook data schema
 */
export const tikTokShopFBTOrderStatusUpdateWebhookDataSchema = z.union([
  tikTokShopFBTOrderStatusUpdateWebhookSchema,
  z.object({
    type: z.literal(TiktokWebhookType[SupportedTikTokShopWebhookTopics.FBT_ORDER_STATUS_UPDATE]),
    seller_open_id: z.string().optional(),
    tts_notification_id: z.string().optional(),
    timestamp: z.string(),
    data: z
      .object({
        inbound_order_id: z.string().optional(),
        order_status: z.string().optional(),
        update_time: z.string().optional(),
      })
      .optional(),
  }),
])

/**
 * TikTok Shop FBT Merchant Onboarding Webhook Schema
 * Webhook Documentation: https://partner.tiktokshop.com/docv2/page/22-fbt-merchant-onboarding
 */
const tikTokShopFBTMerchantOnboardingWebhookSchema = z.object({
  type: z.literal(TiktokWebhookType[SupportedTikTokShopWebhookTopics.FBT_MERCHANT_ONBOARDING]),
  tts_notification_id: z.string().optional(),
  seller_open_id: z.string(),
  timestamp: z.number(),
  data: z.object({
    onboarded_regions: z.array(
      z.object({
        region_code: z.string(),
      }),
    ),
    update_time: z.coerce.string(),
  }),
})

/**
 * TikTok Shop FBT Goods Match Webhook Schema
 * Webhook Documentation: https://partner.tiktokshop.com/docv2/page/23-fbt-goods-match
 */
const tikTokShopFBTGoodsMatchWebhookSchema = z.object({
  type: z.literal(TiktokWebhookType[SupportedTikTokShopWebhookTopics.FBT_GOODS_MATCH]),
  tts_notification_id: z.string().optional(),
  seller_open_id: z.string(),
  timestamp: z.number(),
  data: z.object({
    match_type: z.enum(['MATCH', 'UNMATCH']),
    product_type: z.enum(['LOCAL', 'GLOBAL']),
    goods_id: z.string(),
    product_id: z.string(),
    sku_id: z.string(),
    update_time: z.coerce.string(),
  }),
})

/**
 * TikTok Shop FBT Order Return Status Change Webhook Schema
 * Webhook Documentation: https://partner.tiktokshop.com/docv2/page/12-return-status-change
 */
const tikTokShopFBTOrderReturnStatusChangeWebhookSchema = z.object({
  type: z.literal(
    TiktokWebhookType[SupportedTikTokShopWebhookTopics.FBT_ORDER_RETURN_STATUS_CHANGE],
  ),
  tts_notification_id: z.string().optional(),
  shop_id: z.string().optional(),
  timestamp: z.number(),
  data: z.object({
    order_id: z.string().optional(),
    return_role: z.string().optional(),
    return_type: z.string().optional(),
    return_status: z.string().optional(),
    return_id: z.string().optional(),
    create_time: z.coerce.string().optional(),
    update_time: z.coerce.string().optional(),
  }),
})

/**
 * TikTok Shop Auth Expire Webhook Schema
 * Webhook Documentation: https://partner.tiktokshop.com/docv2/page/7-upcoming-authorization-expiration
 */
const tikTokShopAuthExpireWebhookSchema = z.object({
  type: z.literal(TiktokWebhookType[SupportedTikTokShopWebhookTopics.AUTH_EXPIRE]),
  tts_notification_id: z.string().optional(),
  shop_id: z.string().optional(),
  timestamp: z.number(),
  data: z.object({
    message: z.string().optional(),
    expiration_time: z.coerce.string().optional(),
  }),
})

/**
 * TikTok Shop Inventory Status Change Webhook Schema
 * Webhook Documentation: https://partner.tiktokshop.com/docv2/page/27-inventory-status-change
 */
const tikTokShopInventoryStatusChangeWebhookSchema = z.object({
  type: z.literal(TiktokWebhookType[SupportedTikTokShopWebhookTopics.INVENTORY_STATUS_CHANGE]),
  tts_notification_id: z.string().optional(),
  shop_id: z.string().optional(),
  timestamp: z.number(),
  data: z.object({
    product_id: z.string().optional(),
    sku_id: z.string().optional(),
    trigger_reason: z
      .object({
        alert_type: z.string().optional(),
        lead_days: z.number().optional(),
        low_stock_threshold: z.number().optional(),
      })
      .optional(),
    current_inventory_status: z.string().optional(),
    inventory_distribution: z
      .object({
        total_quantity: z.number().optional(),
        available_quantity: z.number().optional(),
        creator_reserved_quantity: z.number().optional(),
        campaign_reserved_quantity: z.number().optional(),
        committed_quantity: z.number().optional(),
      })
      .optional(),
    // Injected by the webhook handler from Product Inventory Search — per-SKU, per warehouse.
    seller_warehouse_inventory: z
      .array(
        z.object({
          warehouse_id: z.string().optional(),
          warehouse_name: z.string().optional(),
          on_hand_detail: z
            .object({
              total_quantity: z.number().optional(),
              available_quantity: z.number().optional(),
              locked_quantity: z.number().optional(),
            })
            .optional(),
        }),
      )
      .optional(),
    update_time: z.coerce.string().optional(),
  }),
})

/**
 * TikTok Shop Invoice Status Change Webhook Schema
 * Webhook Documentation: https://partner.tiktokshop.com/docv2/page/36-invoice-status-change
 */
const tikTokShopInvoiceStatusChangeWebhookSchema = z.object({
  type: z.literal(TiktokWebhookType[SupportedTikTokShopWebhookTopics.INVOICE_STATUS_CHANGE]),
  tts_notification_id: z.string().optional(),
  shop_id: z.string().optional(),
  timestamp: z.number(),
  data: z.object({
    package_id: z.string().optional(),
    order_ids: z.array(z.string()).optional(),
    invoice_status: z.string().optional(),
    invalid_reason: z.string().optional(),
  }),
})

export const tikTokShopWebhookBodySchema = z.discriminatedUnion('type', [
  tikTokShopOrderChangedWebhookSchema,
  tikTokShopFBTInventoryUpdateWebhookSchema,
  tikTokShopFBTOrderStatusUpdateWebhookSchema,
  tikTokShopFBTMerchantOnboardingWebhookSchema,
  tikTokShopFBTGoodsMatchWebhookSchema,
  tikTokShopAuthExpireWebhookSchema,
  tikTokShopFBTOrderReturnStatusChangeWebhookSchema,
  tikTokShopInventoryStatusChangeWebhookSchema,
  tikTokShopInvoiceStatusChangeWebhookSchema,
])

export type TikTokShopWebhookBody = z.infer<typeof tikTokShopWebhookBodySchema>
